import { Injectable, Injector } from '@angular/core';
import { Moment } from 'moment';
import { CurrentContextService } from '../../core/current-context.service';
import TimeUtils from '../utils/time-utils';

@Injectable()
export class DateService {
  // Lazy to break circular DI: CurrentContextService -> SubscriptionService -> DateService.
  private _currentContextService: CurrentContextService | undefined;

  constructor(private injector: Injector) {}

  private get currentContextService(): CurrentContextService {
    if (!this._currentContextService) {
      this._currentContextService = this.injector.get(CurrentContextService);
    }
    return this._currentContextService;
  }

  private daysInCurrentMonth(): number {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  }

  public calculateDaysUntilReset(dayInMonth: number): number {
    const now = this.nowLocal();
    const resetDayInMonthHavePassed = now.getDate() >= dayInMonth;
    if (resetDayInMonthHavePassed) {
      return this.daysInCurrentMonth() - now.getDate() + dayInMonth;
    }
    return dayInMonth - now.getDate();
  }

  public getDateTimeNowFormatted(addDays?: number): string {
    let today = this.nowLocal();
    if (addDays) {
      today = this.addDays(today, addDays);

      // If might get 0, then it should be first day in month
      if (today.getDate() === 0) {
        today.setDate(1);
      }
    }
    return this.format(today);
  }

  public format(date: Date): string {
    const dayTyped = date.getDate();
    let day = dayTyped.toString();
    const monthTyped = date.getMonth() + 1; // January is 0!
    let month = monthTyped.toString();
    const yearTyped = date.getFullYear();

    if (dayTyped < 10) {
      day = '0' + dayTyped;
    }

    if (monthTyped < 10) {
      month = '0' + monthTyped;
    }

    return yearTyped + '-' + month + '-' + day;
  }

  public dateToUnix(date: Date): number {
    return Math.round(date.getTime() / 1000);
  }

  public unixToDate(unix: number): Moment {
    return TimeUtils.getDateUtcFormat(unix, this.getTimeZoneOffsetMinutes(), false);
  }

  public nowLocal(): Date {
    return new Date();
  }

  public addDays(date: Date, addDays: number): Date {
    // Increase or decrease (positive / negative value) the date by X number of days
    date.setDate(date.getDate() + addDays);
    return date;
  }

  public utcToLocalTime(utcDateString) {
    // Do not treat the string as a UTC-date (if we do, we can not use our timeZoneOffset)
    if (utcDateString.endsWith('Z')) {
      utcDateString = utcDateString.slice(0, -1);
    }
    const utcDate = new Date(utcDateString);

    // Use the users timeZoneOffset to recalculate the date
    const targetTimeZoneOffset = this.getTimeZoneOffsetMinutes();
    return new Date(utcDate.getTime() + targetTimeZoneOffset * 60000);
  }

  public isOlderThanOneDay(dateUnix: number): boolean {
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    const oneDayAgoUnix = this.dateToUnix(oneDayAgo);
    return oneDayAgoUnix > dateUnix;
  }

  public isOlderThanNineMonths(dateUnix: number): boolean {
    const nineMonthsAgo = new Date();
    nineMonthsAgo.setMonth(nineMonthsAgo.getMonth() - 9);
    const nineMonthsAgoUnix = this.dateToUnix(nineMonthsAgo);
    return nineMonthsAgoUnix > dateUnix;
  }

  private getTimeZoneOffsetMinutes(): number {
    const timeZoneId = this.currentContextService.currentContext?.user?.timeZoneId;
    if (!timeZoneId) {
      return -new Date().getTimezoneOffset();
    }
    try {
      const now = new Date();
      const utcMs = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' })).getTime();
      const targetMs = new Date(now.toLocaleString('en-US', { timeZone: timeZoneId })).getTime();
      return (targetMs - utcMs) / 60000;
    } catch {
      return -new Date().getTimezoneOffset();
    }
  }
}

export class DateRange {
  // TODO: Global class, move it
  startDate: string;
  endDate: string;
  isUtcDates: boolean;
}
